"use client";
import { ApiProvider, AuthToken, useApiReady } from "./api/api-provider.js";
import { useWorkOsApiUrl } from "./lib/widgets-context.js";
import {
  ApiKeysLoading,
  ApiKeys as ApiKeysPresentational,
  ApiKeysError,
} from "./lib/api-keys/api-keys.js";
import type {
  ApiKeysLoadingProps,
  ApiKeysErrorProps,
} from "./lib/api-keys/api-keys.js";
import { useListOrganizationApiKeyPermissions } from "./api/endpoint.js";
import * as React from "react";
import { ErrorBoundary } from "./lib/error-boundary.js";
import { usePermissions } from "./lib/use-permissions.js";
import {
  ApiKeysContextProvider,
  useApiKeysContext,
} from "./lib/api-keys/api-keys-context.js";
import { useApiKeys } from "./lib/api/api-key.js";
import { CreateApiKeyDialog } from "./lib/api-keys/create-api-key.js";
import type { WidgetRootDomProps } from "./lib/utils.js";

interface ApiKeysProps extends WidgetRootDomProps {
  authToken: AuthToken;
}

const ApiKeys: React.FC<ApiKeysProps> = ({ authToken, ...domProps }) => {
  const baseUrl = useWorkOsApiUrl();
  return (
    <ErrorBoundary
      fallbackRender={({ error }) => (
        <ApiKeysError {...domProps} error={error} />
      )}
    >
      <ApiProvider
        widgetType="api-keys"
        authToken={authToken}
        baseUrl={baseUrl}
      >
        <ApiKeysContextProvider>
          <CreateApiKeyDialog />
          <ApiKeysImpl {...domProps} />
        </ApiKeysContextProvider>
      </ApiProvider>
    </ErrorBoundary>
  );
};

const ApiKeysImpl = (props: WidgetRootDomProps) => {
  const isApiReady = useApiReady();
  const apiKeys = useApiKeys();
  const { state } = useApiKeysContext();
  useListOrganizationApiKeyPermissions({ limit: 100 });
  const permissionsQuery = usePermissions("widgets:api-keys:manage");

  if (!isApiReady || apiKeys.isLoading || permissionsQuery.isLoading) {
    return <ApiKeysLoading {...props} />;
  }

  if (permissionsQuery.isError || apiKeys.isError) {
    return (
      <ApiKeysError
        error={permissionsQuery.error || apiKeys.error}
        {...props}
      />
    );
  }

  if (apiKeys.isSuccess) {
    return (
      <ApiKeysPresentational
        apiKeys={apiKeys.data}
        searchQuery={state.searchQuery}
        {...props}
      />
    );
  }

  return <ApiKeysError error={apiKeys.error} {...props} />;
};

export type { ApiKeysProps, ApiKeysLoadingProps, ApiKeysErrorProps };
export { ApiKeys, ApiKeysLoading, ApiKeysError };
